import { CommunicationProvider, useCommunication } from "@/lib/CommunicationWrapper"
import { CLOUDFLARE_URL } from "@/lib/config"
import { useState, useEffect, useRef } from "react"
import { History, Check, Sparkles, BookOpen, Zap, RotateCcwSquare, Palette, ChevronUp, ChevronDown, ArrowLeftRight, Gift, X, CheckCircle2, PartyPopper } from "lucide-react"
import { useAuth } from "@/lib/auth"
import Confetti from 'react-confetti'

const UpgradeContent = () => {
    const { sendToBackgroundScript, authenticatedFetch, forceRefreshToken, removeSharedComponent } = useCommunication();
    const { hasUsedTrial, planType } = useAuth();
    const [loading, setLoading] = useState<string | null>(null)
    const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('yearly')
    const [prices, setPrices] = useState<Record<string, { amount: number; currency: string }>>({})
    const [pricesLoading, setPricesLoading] = useState(true)
    const [subscriptionActivated, setSubscriptionActivated] = useState(false)
    const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight })

    const plans = [
        {
            name: "Free",
            monthlyPrice: "$0",
            yearlyPrice: "$0",
            description: "Basic tools to get started",
            subdescription: "Everything in OG BetterCanvas",
            features: [
                "Use and create themes",
                "Basic task planning",
                "Basic notes",
                "Basic calendar sync"
            ],
            image: null,
            radialGradient: "radial-gradient(at 20% -30%, rgba(156, 163, 175, 0.15) 0, transparent 50%), radial-gradient(at 50% -10%, rgba(156, 163, 175, 0.1) 0, transparent 50%), #fff",
            border: "border-[#ededed]",
            buttonStyle: "bg-gray-100 text-gray-600 hover:bg-gray-200",
            monthlyKey: null,
            yearlyKey: null,
        },
        {
            name: "Style+",
            monthlyPrice: "$3", // Fallback, will be replaced
            yearlyPrice: "$30", // Fallback, will be replaced
            description: "Fits your vibe",
            subdescription: "Everything in Free, and",
            features: [
                "Unlimited creations",
                "Auto-rotate themes",
                "Theme history",
                "Advanced theme controls"
            ],
            image: "/style_guy.png",
            radialGradient: "radial-gradient(at 20% -30%, rgb(176 240 255) 0px, rgba(0, 0, 0, 0) 40%), radial-gradient(at 50% -10%, rgb(221 209 255) 0px, rgba(0, 0, 0, 0) 40%), radial-gradient(at 90% 10%, rgb(128 159 255) 0px, rgba(0, 0, 0, 0) 40%), rgb(255, 255, 255)",
            border: "border-[#ece7ff]",
            buttonStyle: "bg-brand-gradient text-white hover:brightness-95 shadow-lg shadow-orange-500/20",
            monthlyKey: "style_monthly",
            yearlyKey: "style_annually",
            popular: false,
        },
        {
            name: "Learn+",
            monthlyPrice: "$11", // Fallback, will be replaced
            yearlyPrice: "$110", // Fallback, will be replaced
            image: "/upgrade_guy.png",
            description: "Save time and do better",
            subdescription: "Everything in Style+, and",
            features: [
                "Study guide from your files",
                "Auto notes from lectures",
                "Unlimited task planning",
                "GPA and grade tracking",
            ],
            radialGradient: "radial-gradient(at 20% -30%, rgb(255 230 70) 0px, rgba(0, 0, 0, 0) 40%), radial-gradient(at 50% -10%, rgb(255 127 18) 0px, rgba(0, 0, 0, 0) 40%), radial-gradient(at 90% 10%, rgb(255 0 99) 0px, rgba(0, 0, 0, 0) 40%), rgb(255, 255, 255)",
            border: "border-[#ffe1e7]",
            buttonStyle: "bg-brand-gradient text-white hover:brightness-95 shadow-lg shadow-blue-500/20",
            monthlyKey: "learn_monthly",
            yearlyKey: "learn_annually",
            popular: true,
        },
    ]

    // Fetch prices from Stripe on mount
    useEffect(() => {
        const fetchPrices = async () => {
            try {
                const response = await authenticatedFetch(`${CLOUDFLARE_URL}/users/get-stripe-prices`, {
                    method: "GET",
                })

                if (!response.ok) {
                    console.error("Failed to fetch prices:", await response.json())
                    setPricesLoading(false)
                    return
                }

                const data = await response.json()
                setPrices(data.message?.prices || {})
            } catch (error) {
                console.error("Error fetching prices:", error)
            } finally {
                setPricesLoading(false)
            }
        }

        fetchPrices()
    }, [authenticatedFetch])

    // Helper function to format price from Stripe amount (in cents)
    const formatPrice = (key: string | null, fallback: string): string => {
        if (!key || !prices[key]) {
            return fallback
        }
        const amount = prices[key].amount / 100 // Convert cents to dollars
        return `$${amount.toFixed(amount % 1 === 0 ? 0 : 2)}`
    }

    // Get plan with dynamic prices
    const getPlanWithPrices = (plan: typeof plans[0]) => {
        return {
            ...plan,
            monthlyPrice: formatPrice(plan.monthlyKey, plan.monthlyPrice),
            yearlyPrice: formatPrice(plan.yearlyKey, plan.yearlyPrice),
        }
    }

    const handleUpgrade = async (plan: typeof plans[0]) => {
        const priceKey = billingPeriod === 'monthly' ? plan.monthlyKey : plan.yearlyKey;

        if (!priceKey) {
            // Free plan - no action needed
            return
        }

        
        setLoading(plan.name)
        try {
            const origin = window.location.origin

            const response = await authenticatedFetch(`${CLOUDFLARE_URL}/users/create-stripe-checkout`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: {
                    priceKey: priceKey,
                    successUrl: `${origin}/success?origin=${origin}`,
                    cancelUrl: `${origin}/cancel?origin=${origin}`,
                    origin: origin,
                    trial: !hasUsedTrial && plan.name === "Learn+", // Request trial if user hasn't used it
                } as any,
            })

            if (!response.ok) {
                const error = await response.json()
                console.error("Failed to create checkout:", error)
                throw new Error(error.error || "Failed to create checkout session")
            }

            const data = await response.json()
            const checkoutUrl = data.message?.checkoutUrl

            if (!checkoutUrl) {
                throw new Error("No checkout URL returned")
            }

            await sendToBackgroundScript({
                type: "OPEN_STRIPE",
                url: checkoutUrl,
                origin: origin
            })
        } catch (error) {
            console.error("Error creating Stripe checkout:", error)
            // TODO: Show error to user
        } finally {
            setLoading(null)
        }
    }

    const condensed = true; // Internal flag for condensed view
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
    const scrollToRef = useRef<HTMLTableElement>(null);
    const [success, setSuccess] = useState(false);

    const toggleCategory = (category: string) => {
        setExpandedCategories(prev => {
            const next = new Set(prev);
            if (next.has(category)) {
                next.delete(category);
            } else {
                next.add(category);
            }
            return next;
        });
    };


    const messageListener = (event: MessageEvent) => {
        if (event.data.type === "STRIPE_SUCCESS_FORWARD") {
            setSuccess(true);
        }
    }

    useEffect(() => {
        window.addEventListener('message', messageListener);
        return () => {
            window.removeEventListener('message', messageListener);
        }
    }, []);

    useEffect(() => {
        const handleResize = () => {
            setWindowSize({ width: window.innerWidth, height: window.innerHeight });
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleExit = () => {
        window.parent.postMessage({ type: "EXIT" }, "*");
    };


    if (planType === "pro" || planType === "trial" || planType === "style") {
        return (
            <div className="flex flex-col h-full overflow-y-auto items-center justify-center min-h-screen bg-gradient-to-b from-orange-50/30 via-white to-white relative">
                <Confetti
                    width={windowSize.width}
                    height={windowSize.height}
                    recycle={false}
                    numberOfPieces={100}
                    gravity={0.2}
                    initialVelocityY={-15}
                    wind={0.02}
                    tweenDuration={500}
                    drawShape={(ctx) => {
                        // Draw confetti pieces with orange/pink gradient theme
                        const colors = ['#ff6b6b', '#ffa952', '#ff686c', '#ff8c42', '#ff6b9d', '#ff8c69', '#ffa07a', '#ffb347'];
                        const randomColor = colors[Math.floor(Math.random() * colors.length)];
                        ctx.fillStyle = randomColor;
                        ctx.beginPath();
                        ctx.arc(0, 0, 5, 0, 2 * Math.PI);
                        ctx.fill();
                    }}
                />
                <div className="px-10 pt-8 pb-12 text-center max-w-2xl mx-auto">
                    {/* Success Icon */}
                    <div className="mb-6 flex justify-center">
                        <div className="relative">
                            <div className="absolute inset-0 bg-orange-500/20 rounded-full blur-2xl animate-pulse" />
                            <CheckCircle2 className="relative size-20 text-green-500" strokeWidth={2} />
                        </div>
                    </div>

                    {/* Main Heading */}
                    <h1 className="text-5xl font-bold text-black mb-4 tracking-wide" style={{ fontFamily: "'Instrument Serif', serif" }}>
                        You've been upgraded!
                    </h1>

                    <p className="text-gray-600 mb-4">Now experience the full power of BetterCampus!</p>


                    {/* Success Guy Image - Larger and more prominent */}
                    <div className="mb-8 flex justify-center">
                        <div className="relative">
                            <div className="absolute inset-0 bg-gradient-to-br from-orange-200/40 to-pink-200/40 rounded-full blur-3xl -z-10" />
                            <img
                                src="/success_guy.png"
                                className="size-48 object-contain mx-auto drop-shadow-lg"
                                alt="Success celebration"
                            />
                        </div>
                    </div>

                    {/* Status Message */}
                    <div className="space-y-6">
                        <div>
                            <button
                                onClick={() => removeSharedComponent("UpgradeModal")}
                                className="inline-flex items-center gap-2 px-8 py-3 bg-brand-gradient text-white font-semibold rounded-xl hover:brightness-95 transition-all duration-200 shadow-lg shadow-orange-500/20 hover:shadow-xl hover:shadow-orange-500/30"
                            >
                                Let's go!
                            </button>
                        </div>
                    </div>

                </div>
            </div>
        )
    }

    return (
        <div className="flex flex-col h-full overflow-y-auto">

            {/* Header */}
            <div className="px-10 pt-8 pb-5 text-center">
                <button className="absolute top-4 right-4 size-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors" onClick={() => removeSharedComponent("UpgradeModal")}><X className="size-4" /></button>
                <div className="flex items-center gap-2 mb-6">
                    <img src="/extlogo.png" className="size-4.5 object-contain" />
                    <h3 className="text font-semibold text-black">BetterCampus</h3>
                </div>
                <h1 className="text-4xl font-bold text-black mb-3 tracking-wide" style={{ fontFamily: "'Instrument Serif', serif" }}>
                    Study smarter. Organize faster. Stress less.
                </h1>
                <p className="text-gray-600 max-w-2xl mx-auto mb-5">
                    Tools that help you study faster, plan assignments, and stay on track.
                </p>

                {/* Billing Period Toggle */}
                <div className="flex items-center justify-center gap-4 relative">
                    <span className={`text-sm font-medium ${billingPeriod === 'monthly' ? 'text-black' : 'text-gray-500'}`}>
                        Monthly
                    </span>
                    <button
                        onClick={() => setBillingPeriod(billingPeriod === 'monthly' ? 'yearly' : 'monthly')}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${billingPeriod === 'yearly' ? 'bg-brand-gradient' : 'bg-gray-300'
                            }`}
                    >
                        <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${billingPeriod === 'yearly' ? 'translate-x-6' : 'translate-x-1'
                                }`}
                        />
                    </button>
                    <span className={`text-sm font-medium relative ${billingPeriod === 'yearly' ? 'text-black' : 'text-gray-500'}`}>
                        Yearly
                        <span className="w-max ml-2 absolute -right-2 translate-x-full ml-2 px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                            Save 17%
                        </span>
                    </span>
                </div>
            </div>

            {/* Plans Grid */}
            <div className="px-10 pb-4 flex-1">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-7xl mx-auto">
                    {plans.map((plan, index) => {
                        const planWithPrices = getPlanWithPrices(plan)
                        return (
                            <div key={plan.name} className="relative">
                                {planWithPrices.image && <img src={planWithPrices.image} className="z-10 absolute top-[-5%] right-[-15%] size-[110px] object-contain" />}
                                {/* Popular Badge */}
                                {planWithPrices.popular && (
                                    <div className="absolute translate-y-[-50%] left-1/2 -translate-x-1/2 z-20">
                                        <span className="bg-brand-gradient text-white px-4 py-1 rounded-full text-xs font-semibold">
                                            Most Popular
                                        </span>
                                    </div>
                                )}
                                <div
                                    className={`
                                relative rounded-2xl shadow-lg bg-white overflow-hidden group
                                transition-all duration-300 border-2 ${planWithPrices.border}
                                flex flex-col
                            `}
                                >
                                    {/* Radial Gradient Background */}
                                    <div
                                        className="absolute inset-0 rounded-2xl transition-opacity duration-300 group-hover:opacity-90 w-[200%] left-[-50%] top-[-33%]"
                                        style={{ background: planWithPrices.radialGradient }}
                                    />

                                    {/* Subtle overlay for better text readability */}
                                    <div className="absolute inset-0 rounded-2xl bg-white/30" />


                                    {/* Content */}
                                    <div className="relative z-10 py-5 px-6 flex flex-col flex-1">
                                        {/* Plan Icon */}


                                        {/* Plan Name */}
                                        <h3 className="text-xl font-bold text-black tracking-wide" style={{ fontFamily: "'Instrument Serif', serif" }}>{planWithPrices.name}</h3>
                                        <p className="text-sm text-gray-600 mb-3">{planWithPrices.description}</p>


                                        {/* Price */}
                                        <div className="mb-1">
                                            {pricesLoading ? (
                                                <div className="h-12 flex items-center">
                                                    <span className="w-4 h-4 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="flex gap-2 items-center tracking-wider">
                                                        <span className="text-3xl font-bold text-black" >
                                                            {billingPeriod === 'yearly'
                                                                ? `$${(parseFloat(planWithPrices.yearlyPrice.replace('$', '')) / 12).toFixed(2)}`
                                                                : planWithPrices.monthlyPrice
                                                            }
                                                        </span>
                                                        {planWithPrices.name !== "Free" && (
                                                            <div className="text-gray-500 text-xs font-semibold font-sans tracking-normal" >
                                                                <p>per month</p>
                                                                {billingPeriod === 'yearly' && <p>billed yearly</p>}
                                                            </div>
                                                        )}
                                                    </div>
                                                </>
                                            )}
                                        </div>

                                        <button
                                            onClick={() => handleUpgrade(plan)}
                                            disabled={loading === plan.name || pricesLoading || !(billingPeriod === 'monthly' ? plan.monthlyKey : plan.yearlyKey)}
                                            className={`
                                        w-full py-2 px-4 rounded-xl font-semibold
                                        transition-all duration-200 mt-1 flex items-center justify-center gap-2 text-md
                                        ${(billingPeriod === 'monthly' ? plan.monthlyKey : plan.yearlyKey) ? planWithPrices.buttonStyle : planWithPrices.buttonStyle}
                                        ${loading === plan.name || pricesLoading ? 'opacity-50 cursor-not-allowed' : ''}
                                        ${!(billingPeriod === 'monthly' ? plan.monthlyKey : plan.yearlyKey) ? 'cursor-default' : 'cursor-pointer'}
                                    `}
                                        >
                                            {(() => {
                                                if (loading === plan.name) {
                                                    return (
                                                        <>
                                                            <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                                            <span>Processing...</span>
                                                        </>
                                                    )
                                                }

                                                if (!hasUsedTrial && plan.name === "Learn+") {
                                                    return (
                                                        <>
                                                            <Gift className="size-4 text-white" />
                                                            <span className="text-white">Try free for 7 days</span>
                                                        </>
                                                    )
                                                }

                                                if (billingPeriod === 'monthly' ? plan.monthlyKey : plan.yearlyKey) {
                                                    return (
                                                        <span className="text-white">Upgrade Now</span>
                                                    )
                                                }

                                                return (
                                                    <span className="text-gray-500">Current Plan</span>
                                                )
                                            })()}
                                        </button>

                                        {/* Description */}

                                        <p className="text-sm text-black font-semibold mt-4 mb-2">{planWithPrices.subdescription}</p>

                                        {/* Features List */}
                                        <div className="flex-1 mb-1">
                                            <ul className="space-y-2">
                                                {planWithPrices.features.map((feature, idx) => (
                                                    <li key={idx} className="flex items-start gap-2">
                                                        <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                                                        <span className="text-sm text-gray-700">{feature}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>

                                        {/* CTA Button */}
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>

            <div className="relative h-[1px] py-4 w-[66%] mx-auto mb-10">
                <div className="h-[1px] w-full bg-gray-200"></div>
                <button onClick={() => scrollToRef.current?.scrollIntoView({ behavior: 'smooth' })} className="bg-white px-2 translate-y-[-50%]  translate-x-[-50%] absolute left-1/2 text-left text-sm text-gray-500 hover:text-gray-700 transition-colors font-medium relative">Compare plans <ChevronDown className="absolute -bottom-1 translate-y-full size-4 -translate-x-1/2 left-1/2" /></button>
            </div>

            <div className="mx-10 mb-10 backdrop-blur-xs">
                {/*}
                    <div className="px-6 py-4" style={{ background: "linear-gradient(90deg, #ffa952,#ff686c)" }}>
                        <h2 className="text-xl font-bold text-white text-center">
                            Compare Plans
                        </h2>
                    </div>
                    */}

                <div>
                    <table className="w-full" ref={scrollToRef}>
                        <thead>
                            <tr>
                                <th className="text-left font-semibold text-gray-900 px-10 pb-4">What you get</th>
                                <th className="text-center font-semibold text-gray-900 pb-4">Free</th>
                                <th className="text-center font-semibold text-gray-900 pb-4">Style+</th>
                                <th className="text-center font-semibold text-gray-900 pb-4">Learn+</th>
                            </tr>
                        </thead>
                    </table>
                    <div className="rounded-2xl border border-gray-300 overflow-hidden">
                        <table className="w-full">
                            <tbody className="divide-y divide-gray-200">
                                {condensed ? (
                                    <>
                                        {/* Themes & Customization Category */}
                                        <tr className="">
                                            <td className="px-6 py-3">
                                                <button
                                                    onClick={() => toggleCategory('themes')}
                                                    className="flex items-center gap-2 font-semibold text-gray-900 hover:text-orange-600 transition-colors text-left"
                                                >
                                                    {expandedCategories.has('themes') ? (
                                                        <ChevronUp className="size-4 flex-shrink-0" />
                                                    ) : (
                                                        <ChevronDown className="size-4 flex-shrink-0" />
                                                    )}
                                                    <div>
                                                        <div>Themes</div>
                                                        <div className="text-xs font-normal text-gray-500 mt-0.5">
                                                            Theme saving, swaps, auto-rotation
                                                        </div>
                                                    </div>
                                                </button>
                                            </td>
                                            <td className="px-6 py-3 text-center text-sm text-gray-600">
                                                Limited
                                            </td>
                                            <td className="px-6 py-3 text-center text-sm">
                                                <span className="text-orange-600 text-lg">✓</span>
                                            </td>
                                            <td className="px-6 py-3 text-center text-sm bg-orange-50">
                                                <span className="text-orange-600 text-lg">✓</span>
                                            </td>
                                        </tr>
                                        {expandedCategories.has('themes') && (
                                            <>
                                                <tr className="bg-gray-50 transition-colors">
                                                    <td className="px-6 py-4 pl-12">
                                                        <div className="flex items-center">
                                                            <Palette className="size-4 text-gray-600 mr-4" />
                                                            <div>
                                                                <p className="text-sm font-medium text-gray-900">Theme Saving</p>
                                                                <p className="text-xs text-gray-500">Save your creations to your library</p>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <span className="text-gray-600 text-sm">3 themes</span>
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <span className="text-orange-700 font-semibold">Unlimited</span>
                                                    </td>
                                                    <td className="px-6 py-4 text-center bg-orange-50">
                                                        <span className="text-orange-700 font-semibold">Unlimited</span>
                                                    </td>
                                                </tr>
                                                <tr className="bg-gray-50 transition-colors">
                                                    <td className="px-6 py-4 pl-12">
                                                        <div className="flex items-center">
                                                            <ArrowLeftRight className="size-4 text-gray-600 mr-4" />
                                                            <div>
                                                                <p className="text-sm font-medium text-gray-900">Theme Swaps</p>
                                                                <p className="text-xs text-gray-500">Apply themes to your Canvas</p>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <span className="text-gray-600 text-sm">3/week</span>
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <span className="text-orange-700 font-semibold">Unlimited</span>
                                                    </td>
                                                    <td className="px-6 py-4 text-center bg-orange-50">
                                                        <span className="text-orange-700 font-semibold">Unlimited</span>
                                                    </td>
                                                </tr>
                                                <tr className="bg-gray-50 transition-colors">
                                                    <td className="px-6 py-4 pl-12">
                                                        <div className="flex items-center">
                                                            <RotateCcwSquare className="size-4 text-gray-600 mr-4" />
                                                            <div>
                                                                <p className="text-sm font-medium text-gray-900">Auto-rotate theme</p>
                                                                <p className="text-xs text-gray-500">Rotate your theme on a schedule</p>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <span className="text-gray-400">✕</span>
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <span className="text-orange-600 text-xl">✓</span>
                                                    </td>
                                                    <td className="px-6 py-4 text-center bg-orange-50">
                                                        <span className="text-orange-600 text-xl">✓</span>
                                                    </td>
                                                </tr>
                                                <tr className="bg-gray-50 transition-colors">
                                                    <td className="px-6 py-4 pl-12">
                                                        <div className="flex items-center">
                                                            <History className="size-4 text-gray-600 mr-4" />
                                                            <div>
                                                                <p className="text-sm font-medium text-gray-900">Theme History</p>
                                                                <p className="text-xs text-gray-500">See past themes and apply them again</p>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <span className="text-gray-400">✕</span>
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <span className="text-orange-600 text-xl">✓</span>
                                                    </td>
                                                    <td className="px-6 py-4 text-center bg-orange-50">
                                                        <span className="text-orange-600 text-xl">✓</span>
                                                    </td>
                                                </tr>
                                            </>
                                        )}

                                        {/* AI Learning Tools Category */}
                                        <tr className="">
                                            <td className="px-6 py-3">
                                                <button
                                                    onClick={() => toggleCategory('ai')}
                                                    className="flex items-center gap-2 font-semibold text-gray-900 hover:text-orange-600 transition-colors text-left"
                                                >
                                                    {expandedCategories.has('ai') ? (
                                                        <ChevronUp className="size-4 flex-shrink-0" />
                                                    ) : (
                                                        <ChevronDown className="size-4 flex-shrink-0" />
                                                    )}
                                                    <div>
                                                        <div>Study Tools</div>
                                                        <div className="text-xs font-normal text-gray-500 mt-0.5">
                                                            Quizzes, flashcards, chat in files
                                                        </div>
                                                    </div>
                                                </button>
                                            </td>
                                            <td className="px-6 py-3 text-center text-sm text-gray-600">
                                                Limited
                                            </td>
                                            <td className="px-6 py-3 text-center text-sm text-gray-600">
                                                Limited
                                            </td>
                                            <td className="px-6 py-3 text-center text-sm bg-orange-50">
                                                <span className="text-orange-700 font-semibold">Full Access</span>
                                            </td>
                                        </tr>
                                        {expandedCategories.has('ai') && (
                                            <>
                                                <tr className="bg-gray-50 transition-colors">
                                                    <td className="px-6 py-4 pl-12">
                                                        <div className="flex items-center">
                                                            <div>
                                                                <p className="font-medium text-gray-900">AI Study Tools</p>
                                                                <p className="text-sm text-gray-500">Quizzes, flashcards, and chat directly in Canvas files</p>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <span className="text-gray-600 text-sm">Limited</span>
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <span className="text-gray-600 text-sm">Limited</span>
                                                    </td>
                                                    <td className="px-6 py-4 text-center bg-orange-50">
                                                        <span className="text-orange-700 font-semibold">Unlimited</span>
                                                    </td>
                                                </tr>
                                                <tr className="bg-gray-50 transition-colors">
                                                    <td className="px-6 py-4 pl-12">
                                                        <div className="flex items-center">
                                                            <div>
                                                                <p className="font-medium text-gray-900">Lecture Transcriptions</p>
                                                                <p className="text-sm text-gray-500">Record your lectures and never miss a moment</p>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <span className="text-gray-600 text-sm">10 min/day</span>
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <span className="text-gray-600 text-sm">10 min/day</span>
                                                    </td>
                                                    <td className="px-6 py-4 text-center bg-orange-50">
                                                        <span className="text-orange-700 font-semibold">6 hr/day</span>
                                                    </td>
                                                </tr>
                                            </>
                                        )}

                                        {/* Notes & Productivity Category */}
                                        <tr className="">
                                            <td className="px-6 py-3">
                                                <button
                                                    onClick={() => toggleCategory('notes')}
                                                    className="flex items-center gap-2 font-semibold text-gray-900 hover:text-orange-600 transition-colors text-left"
                                                >
                                                    {expandedCategories.has('notes') ? (
                                                        <ChevronUp className="size-4 flex-shrink-0" />
                                                    ) : (
                                                        <ChevronDown className="size-4 flex-shrink-0" />
                                                    )}
                                                    <div>
                                                        <div>Productivity tools</div>
                                                        <div className="text-xs font-normal text-gray-500 mt-0.5">
                                                            Smart notes, AI enhancement & assignment planner
                                                        </div>
                                                    </div>
                                                </button>
                                            </td>
                                            <td className="px-6 py-3 text-center text-sm text-gray-600">
                                                6 notes
                                            </td>
                                            <td className="px-6 py-3 text-center text-sm text-gray-600">
                                                6 notes
                                            </td>
                                            <td className="px-6 py-3 text-center text-sm bg-orange-50">
                                                <span className="text-orange-700 font-semibold">Unlimited</span>
                                            </td>
                                        </tr>
                                        {expandedCategories.has('notes') && (
                                            <>
                                                <tr className="bg-gray-50 transition-colors">
                                                    <td className="px-6 py-4 pl-12">
                                                        <div className="flex items-center">
                                                            <div>
                                                                <p className="font-medium text-gray-900">Notes</p>
                                                                <p className="text-sm text-gray-500">Take notes and organize by class</p>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <span className="text-gray-600 text-sm">6 notes</span>
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <span className="text-gray-600 text-sm">6 notes</span>
                                                    </td>
                                                    <td className="px-6 py-4 text-center bg-orange-50">
                                                        <span className="text-orange-700 font-semibold">Unlimited</span>
                                                    </td>
                                                </tr>
                                                <tr className="bg-gray-50 transition-colors">
                                                    <td className="px-6 py-4 pl-12">
                                                        <div className="flex items-center">
                                                            <div>
                                                                <p className="font-medium text-gray-900">AI-Enhanced Smart Notes</p>
                                                                <p className="text-sm text-gray-500">Enhance your notes and combine with lecture transcriptions</p>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <span className="text-gray-600 text-sm">6 notes</span>
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <span className="text-gray-600 text-sm">6 notes</span>
                                                    </td>
                                                    <td className="px-6 py-4 text-center bg-orange-50">
                                                        <span className="text-orange-700 font-semibold">Unlimited</span>
                                                    </td>
                                                </tr>
                                                <tr className="bg-gray-50 transition-colors">
                                                    <td className="px-6 py-4 pl-12">
                                                        <div className="flex items-center">
                                                            <div>
                                                                <p className="font-medium text-gray-900">Assignment Planner</p>
                                                                <p className="text-sm text-gray-500">Schedule & track your assignments</p>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <span className="text-gray-400">✕</span>
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <span className="text-gray-400">✕</span>
                                                    </td>
                                                    <td className="px-6 py-4 text-center bg-orange-50">
                                                        <span className="text-orange-600 text-xl">✓</span>
                                                    </td>
                                                </tr>
                                            </>
                                        )}

                                        {/* Storage & Support Category */}
                                        <tr className="">
                                            <td className="px-6 py-3">
                                                <button
                                                    onClick={() => toggleCategory('storage')}
                                                    className="flex items-center gap-2 font-semibold text-gray-900 hover:text-orange-600 transition-colors text-left"
                                                >
                                                    {expandedCategories.has('storage') ? (
                                                        <ChevronUp className="size-4 flex-shrink-0" />
                                                    ) : (
                                                        <ChevronDown className="size-4 flex-shrink-0" />
                                                    )}
                                                    <div>
                                                        <div>Storage, Analytics & Support</div>
                                                        <div className="text-xs font-normal text-gray-500 mt-0.5">
                                                            Cloud storage, progress tracking & priority help
                                                        </div>
                                                    </div>
                                                </button>
                                            </td>
                                            <td className="px-6 py-3 text-center text-sm text-gray-600">
                                                Basic
                                            </td>
                                            <td className="px-6 py-3 text-center text-sm">
                                                <span className="text-orange-700 font-semibold">Enhanced</span>
                                            </td>
                                            <td className="px-6 py-3 text-center text-sm bg-orange-50">
                                                <span className="text-orange-700 font-semibold">Premium</span>
                                            </td>
                                        </tr>
                                        {expandedCategories.has('storage') && (
                                            <>
                                                <tr className="hover:bg-gray-50 transition-colors">
                                                    <td className="px-6 py-4 pl-12">
                                                        <div className="flex items-center">
                                                            <div>
                                                                <p className="font-medium text-gray-900">Cloud Storage</p>
                                                                <p className="text-sm text-gray-500">Sync your data across devices</p>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <span className="text-gray-600 text-sm">100MB</span>
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <span className="text-orange-700 font-semibold">5GB</span>
                                                    </td>
                                                    <td className="px-6 py-4 text-center bg-orange-50">
                                                        <span className="text-orange-700 font-semibold">50GB</span>
                                                    </td>
                                                </tr>
                                                <tr className="hover:bg-gray-50 transition-colors">
                                                    <td className="px-6 py-4 pl-12">
                                                        <div className="flex items-center">
                                                            <div>
                                                                <p className="font-medium text-gray-900">Learning Analytics</p>
                                                                <p className="text-sm text-gray-500">Track your progress & insights</p>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <span className="text-gray-400">✕</span>
                                                    </td>
                                                    <td className="px-6 py-4 text-center bg-orange-50/30">
                                                        <span className="text-gray-400">✕</span>
                                                    </td>
                                                    <td className="px-6 py-4 text-center bg-orange-50/30">
                                                        <span className="text-orange-600 text-xl">✓</span>
                                                    </td>
                                                </tr>
                                                <tr className="hover:bg-gray-50 transition-colors">
                                                    <td className="px-6 py-4 pl-12">
                                                        <div className="flex items-center">
                                                            <div>
                                                                <p className="font-medium text-gray-900">Priority Support</p>
                                                                <p className="text-sm text-gray-500">Get help when you need it</p>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <span className="text-gray-600 text-sm">Community</span>
                                                    </td>
                                                    <td className="px-6 py-4 text-center bg-orange-50/30">
                                                        <span className="text-orange-700 font-semibold">Email</span>
                                                    </td>
                                                    <td className="px-6 py-4 text-center bg-orange-50/30">
                                                        <span className="text-orange-700 font-semibold">Priority</span>
                                                    </td>
                                                </tr>
                                                <tr className="hover:bg-gray-50 transition-colors">
                                                    <td className="px-6 py-4 pl-12">
                                                        <div className="flex items-center">
                                                            <div>
                                                                <p className="font-medium text-gray-900">Study Groups & Collaboration</p>
                                                                <p className="text-sm text-gray-500">Share notes and study together</p>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <span className="text-gray-400">✕</span>
                                                    </td>
                                                    <td className="px-6 py-4 text-center bg-orange-50/30">
                                                        <span className="text-gray-600 text-sm">3 groups</span>
                                                    </td>
                                                    <td className="px-6 py-4 text-center bg-orange-50/30">
                                                        <span className="text-orange-700 font-semibold">Unlimited</span>
                                                    </td>
                                                </tr>
                                            </>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        {/* Feature 1: Custom Themes */}
                                        <tr className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center">
                                                    <Palette className="size-4 text-gray-600 mr-4" />
                                                    <div>
                                                        <p className="font-medium text-gray-900">Theme Saving</p>
                                                        <p className="text-sm text-gray-500">Save your creations to your library</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <span className="text-gray-600 text-sm">3 themes</span>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <span className="text-orange-700 font-semibold">Unlimited</span>
                                            </td>
                                            <td className="px-6 py-4 text-center bg-orange-50">
                                                <span className="text-orange-700 font-semibold">Unlimited</span>
                                            </td>
                                        </tr>

                                        {/* Feature 2: Theme Library Access */}
                                        <tr className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center">
                                                    <ArrowLeftRight className="size-4 text-gray-600 mr-4" />
                                                    <div>
                                                        <p className="font-medium text-gray-900">Theme Swaps</p>
                                                        <p className="text-sm text-gray-500">Apply themes to your Canvas</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <span className="text-gray-600 text-sm">3/week</span>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <span className="text-orange-700 font-semibold">Unlimited</span>
                                            </td>
                                            <td className="px-6 py-4 text-center bg-orange-50">
                                                <span className="text-orange-700 font-semibold">Unlimited</span>
                                            </td>
                                        </tr>

                                        <tr className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center">
                                                    <RotateCcwSquare className="size-4 text-gray-600 mr-4" />
                                                    <div>
                                                        <p className="font-medium text-gray-900">Auto-rotate theme</p>
                                                        <p className="text-sm text-gray-500">Rotate your theme on a schedule</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <span className="text-gray-400">✕</span>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <span className="text-orange-600 text-xl">✓</span>
                                            </td>
                                            <td className="px-6 py-4 text-center bg-orange-50">
                                                <span className="text-orange-600 text-xl">✓</span>
                                            </td>
                                        </tr>

                                        {/* Feature 3: AI-Powered Study Assistant */}
                                        <tr className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center">
                                                    <div>
                                                        <p className="font-medium text-gray-900">AI Study Tools</p>
                                                        <p className="text-sm text-gray-500">Quizzes, flashcards, and chat directly in Canvas files</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <span className="text-gray-600 text-sm">Limited</span>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <span className="text-gray-600 text-sm">Limited</span>
                                            </td>
                                            <td className="px-6 py-4 text-center bg-orange-50">
                                                <span className="text-orange-700 font-semibold">Unlimited</span>
                                            </td>
                                        </tr>

                                        <tr className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center">
                                                    <div>
                                                        <p className="font-medium text-gray-900">Lecture Transcriptions</p>
                                                        <p className="text-sm text-gray-500">Record your lectures and never miss a moment</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <span className="text-gray-600 text-sm">10 min/day</span>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <span className="text-gray-600 text-sm">10 min/day</span>
                                            </td>
                                            <td className="px-6 py-4 text-center bg-orange-50">
                                                <span className="text-orange-700 font-semibold">6 hr/day</span>
                                            </td>
                                        </tr>

                                        <tr className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center">
                                                    <div>
                                                        <p className="font-medium text-gray-900">Notes</p>
                                                        <p className="text-sm text-gray-500">Take notes and organize by class</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <span className="text-gray-600 text-sm">6 notes</span>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <span className="text-gray-600 text-sm">6 notes</span>
                                            </td>
                                            <td className="px-6 py-4 text-center bg-orange-50">
                                                <span className="text-orange-700 font-semibold">Unlimited</span>
                                            </td>
                                        </tr>

                                        <tr className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center">
                                                    <div>
                                                        <p className="font-medium text-gray-900">AI-Enhanced Smart Notes</p>
                                                        <p className="text-sm text-gray-500">Enhance your notes and combine with lecture transcriptions</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <span className="text-gray-600 text-sm">6 notes</span>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <span className="text-gray-600 text-sm">6 notes</span>
                                            </td>
                                            <td className="px-6 py-4 text-center bg-orange-50">
                                                <span className="text-orange-700 font-semibold">Unlimited</span>
                                            </td>
                                        </tr>

                                        {/* Feature 4: Smart Notes & Highlights */}

                                        {/* Feature 5: Assignment Planner */}
                                        <tr className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center">
                                                    <div>
                                                        <p className="font-medium text-gray-900">Assignment Planner</p>
                                                        <p className="text-sm text-gray-500">Schedule & track your assignments</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <span className="text-gray-400">✕</span>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <span className="text-gray-400">✕</span>
                                            </td>
                                            <td className="px-6 py-4 text-center bg-orange-50">
                                                <span className="text-orange-600 text-xl">✓</span>
                                            </td>
                                        </tr>

                                        {/* Feature 6: Advanced Customization */}
                                        <tr className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center">
                                                    <History className="size-4 text-gray-600 mr-4" />
                                                    <div>
                                                        <p className="font-medium text-gray-900">Theme History</p>
                                                        <p className="text-sm text-gray-500">View your past themes and apply them again</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <span className="text-gray-400">✕</span>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <span className="text-orange-600 text-xl">✓</span>
                                            </td>
                                            <td className="px-6 py-4 text-center bg-orange-50">
                                                <span className="text-orange-600 text-xl">✓</span>
                                            </td>
                                        </tr>

                                        {/* Feature 7: Cloud Storage */}
                                        <tr className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center">
                                                    <div>
                                                        <p className="font-medium text-gray-900">Cloud Storage</p>
                                                        <p className="text-sm text-gray-500">Sync your data across devices</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <span className="text-gray-600 text-sm">100MB</span>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <span className="text-orange-700 font-semibold">5GB</span>
                                            </td>
                                            <td className="px-6 py-4 text-center bg-orange-50">
                                                <span className="text-orange-700 font-semibold">50GB</span>
                                            </td>
                                        </tr>

                                        {/* Feature 8: Learning Analytics */}
                                        <tr className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center">
                                                    <div>
                                                        <p className="font-medium text-gray-900">Learning Analytics</p>
                                                        <p className="text-sm text-gray-500">Track your progress & insights</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <span className="text-gray-400">✕</span>
                                            </td>
                                            <td className="px-6 py-4 text-center bg-orange-50/30">
                                                <span className="text-gray-400">✕</span>
                                            </td>
                                            <td className="px-6 py-4 text-center bg-orange-50/30">
                                                <span className="text-orange-600 text-xl">✓</span>
                                            </td>
                                        </tr>

                                        {/* Feature 9: Priority Support */}
                                        <tr className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center">
                                                    <div>
                                                        <p className="font-medium text-gray-900">Priority Support</p>
                                                        <p className="text-sm text-gray-500">Get help when you need it</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <span className="text-gray-600 text-sm">Community</span>
                                            </td>
                                            <td className="px-6 py-4 text-center bg-orange-50/30">
                                                <span className="text-orange-700 font-semibold">Email</span>
                                            </td>
                                            <td className="px-6 py-4 text-center bg-orange-50/30">
                                                <span className="text-orange-700 font-semibold">Priority</span>
                                            </td>
                                        </tr>

                                        {/* Feature 10: Study Groups & Collaboration */}
                                        <tr className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center">
                                                    <div>
                                                        <p className="font-medium text-gray-900">Study Groups & Collaboration</p>
                                                        <p className="text-sm text-gray-500">Share notes and study together</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <span className="text-gray-400">✕</span>
                                            </td>
                                            <td className="px-6 py-4 text-center bg-orange-50/30">
                                                <span className="text-gray-600 text-sm">3 groups</span>
                                            </td>
                                            <td className="px-6 py-4 text-center bg-orange-50/30">
                                                <span className="text-orange-700 font-semibold">Unlimited</span>
                                            </td>
                                        </tr>
                                    </>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    )
}

export const Upgrade = () => {
    return (
        <CommunicationProvider>
            <UpgradeContent />
        </CommunicationProvider>
    )
}
